import { ShoppingCart, PaperPlaneRight, CheckCircle, Clock } from '@phosphor-icons/react';
import { apiClient } from '@dukene/shared';

interface AbandonedCart {
  id: string;
  customerName: string;
  customerEmail: string;
  itemsCount: number;
  totalValue: number;
  abandonedAt: string;
  recoveryStatus: 'PENDING' | 'SENT' | 'RECOVERED' | string;
}

export default function AbandonedCartsTable({ data = [] }: { data?: AbandonedCart[] }) {
  const handleSendRecovery = async (id: string) => {
    try {
      await apiClient.post(`/analytics/cart/${id}/send-recovery`);
      alert('Recovery email sent successfully!');
    } catch (error) {
      alert('Failed to send recovery email.');
    }
  };

  const carts = data;

  return (
    <div className="bg-white dark:bg-[#1A1D21] rounded-xl border border-[#D2D5D9] dark:border-slate-800 shadow-sm overflow-hidden">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 p-4 border-b border-[#E1E4E8] dark:border-slate-800 bg-[#F6F6F7]/50 dark:bg-slate-900/40">
        <div>
          <h3 className="text-sm font-bold text-black dark:text-white">
            Abandoned Checkouts & Carts
          </h3>
          <p className="text-xs text-gray-500 mt-0.5">
            Shoppers who placed items in cart or started checkout without finishing payment
          </p>
        </div>

        <div className="flex items-center gap-2">
          <span className="h-8 inline-flex items-center gap-1.5 px-3 bg-white dark:bg-slate-800 border border-[#D2D5D9] dark:border-slate-700 rounded-lg text-xs font-semibold text-[#202223] dark:text-slate-300">
            <span>{carts.length} active carts</span>
          </span>
        </div>
      </div>

      {/* Table */}
      <div className="overflow-x-auto">
        <table className="w-full text-left border-collapse">
          <thead>
            <tr className="bg-[#F6F6F7]/60 dark:bg-slate-900/40 border-b border-[#E1E4E8] dark:border-slate-800 text-[11px] font-bold text-[#5C5F62] uppercase tracking-wider">
              <th className="px-5 py-3">Customer</th>
              <th className="px-5 py-3">Cart Contents</th>
              <th className="px-5 py-3">Cart Value</th>
              <th className="px-5 py-3">Abandoned</th>
              <th className="px-5 py-3">Recovery Status</th>
              <th className="px-5 py-3 text-right">Action</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-[#E1E3E5] dark:divide-white/5 text-xs">
            {carts.map((cart) => (
              <tr
                key={cart.id}
                className="hover:bg-[#F9FAFB] dark:hover:bg-white/5 transition-colors"
              >
                <td className="px-5 py-4">
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 rounded-full bg-gray-100 dark:bg-white/10 flex items-center justify-center font-bold text-gray-700 dark:text-gray-200">
                      {cart.customerName.charAt(0)}
                    </div>
                    <div>
                      <div className="font-semibold text-black dark:text-white">
                        {cart.customerName}
                      </div>
                      <div className="text-[11px] text-gray-400">{cart.customerEmail}</div>
                    </div>
                  </div>
                </td>

                <td className="px-5 py-4">
                  <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg bg-gray-100 dark:bg-white/5 text-gray-700 dark:text-gray-300 font-medium">
                    <ShoppingCart weight="fill" className="w-3.5 h-3.5 text-gray-400" />
                    <span>{cart.itemsCount} items</span>
                  </span>
                </td>

                <td className="px-5 py-4 font-mono font-bold text-black dark:text-white">
                  KES {cart.totalValue.toLocaleString()}
                </td>

                <td className="px-5 py-4 text-gray-500">{cart.abandonedAt}</td>

                <td className="px-5 py-4">
                  {cart.recoveryStatus === 'RECOVERED' && (
                    <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-emerald-50 dark:bg-emerald-950/40 text-[#006838] dark:text-emerald-400 font-bold text-[11px]">
                      <CheckCircle weight="fill" className="w-3.5 h-3.5" />
                      Recovered
                    </span>
                  )}
                  {cart.recoveryStatus === 'SENT' && (
                    <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-blue-50 dark:bg-blue-950/40 text-blue-600 dark:text-blue-400 font-bold text-[11px]">
                      <Clock weight="fill" className="w-3.5 h-3.5" />
                      Recovery Sent
                    </span>
                  )}
                  {cart.recoveryStatus === 'PENDING' && (
                    <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-amber-50 dark:bg-amber-950/40 text-amber-600 dark:text-amber-400 font-bold text-[11px]">
                      Pending Recovery
                    </span>
                  )}
                </td>

                <td className="px-5 py-4 text-right">
                  {cart.recoveryStatus !== 'RECOVERED' && (
                    <button
                      onClick={() => handleSendRecovery(cart.id)}
                      className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-xl bg-black dark:bg-white text-white dark:text-black font-semibold hover:bg-black/80 dark:hover:bg-gray-100 transition-colors shadow-2xs"
                    >
                      <PaperPlaneRight weight="fill" className="w-3.5 h-3.5" />
                      <span>{cart.recoveryStatus === 'SENT' ? 'Resend' : 'Send Recovery'}</span>
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
